import { Injectable } from '@angular/core';
import { HttpService } from '../../http/http.service';
import { buildQueryParams, createFileUploadHeaders, createQueryParam } from 'utils/helpers';
import { STEP_COLOR_STATUSES } from 'constants/status.constants';
import { constants } from 'constants/constants';
import { map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { CertificateInstance, EmployerRequestUpdate } from './certificates.interface';
import { ShowIfHttpError } from '@certemy/common';

@Injectable()
export class CertificatesService {
  constructor(private http: HttpService) {}

  getCertificates({
    page = 1,
    page_size = 10,
    search = '',
    isActive = true,
  }): Observable<{ result: CertificateInstance[]; count: number; full_count: number }> {
    const params = [
      createQueryParam('page', page),
      createQueryParam('page_size', page_size),
      createQueryParam('search', search),
      createQueryParam('isActive', isActive),
    ];
    const queryParams = buildQueryParams(params);

    return this.http.get(`/certification_instance${queryParams}`).pipe(
      map((data) => {
        return {
          ...data,
          result: data.result.map((certificate) =>
            certificate.is_active === false ? this.setDisableStatus(certificate) : certificate,
          ),
        };
      }),
    );
  }

  @ShowIfHttpError('Can not get credential details. Please, try again.')
  getCertificate(id) {
    return this.http.get(`/certification_instance/${id}`).pipe(
      map((res) => res.result),
      map((certificate) => (certificate.is_active === false ? this.setDisableStatus(certificate) : certificate)),
    );
  }

  updateCertificateAssignStatus(id, body) {
    return this.http.put(`/certification_instance/${id}`, body);
  }

  uploadCertificateForVerifier(id, body) {
    const options = createFileUploadHeaders();
    return this.http.post(`/certification_instance/${id}/verify`, body, options);
  }

  getRejectComments(id) {
    return this.http.get(`/certification_instance/${id}/reject_comments`).pipe(map((data) => data.result.comments));
  }

  @ShowIfHttpError('An error occurred while updating request.')
  updateEmployerRequestStatus(requestId, requestUpdate: EmployerRequestUpdate) {
    return this.http.put(`/professional/certification/request/${requestId}`, requestUpdate);
  }

  private setDisableStatus(certificate) {
    return {
      ...certificate,
      status_color: STEP_COLOR_STATUSES.DISABLED,
      status_id: constants.CERTIFICATE_STATUSES.DISABLED,
    };
  }

  @ShowIfHttpError('Cannot revoke request')
  revokeEmployerRequest(requestId: number): Observable<null> {
    return this.http.patch(`/professional/request/${requestId}/revoke`, {});
  }

  @ShowIfHttpError('Cannot receive notifications for professional.')
  getNotifications(): Observable<any> {
    return this.http.get(`/profile-notification/step-instance`);
  }

  @ShowIfHttpError('An error occurred. Please try again later.')
  getPhaseInstance(phaseId: number) {
    return this.http.get(`/phase-instance/${phaseId}`);
  }

  @ShowIfHttpError('An error occurred. Please try again later.')
  getPhaseSteps(phaseId: number, certificationId: number) {
    return this.http.get(`/certification-instance/${certificationId}/phases/${phaseId}`);
  }
}
